import { useState } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { Card, CardContent } from '@/components/ui/card';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { useDashboardTimeSeries } from '@/hooks/useDashboard';
import { cn } from '@/lib/cn';
import { useChartTheme } from '@/lib/theme';

type TimeRange = '24h' | '7d' | '30d';

interface RangeButtonProps {
  range: TimeRange;
  currentRange: TimeRange;
  onClick: (range: TimeRange) => void;
  label: string;
}

function RangeButton({ range, currentRange, onClick, label }: RangeButtonProps) {
  const isActive = range === currentRange;
  return (
    <button
      onClick={() => onClick(range)}
      className={cn(
        'px-3 py-1 text-[10px] font-medium uppercase tracking-wider transition-colors',
        isActive
          ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 rounded'
          : 'text-gray-500 hover:text-gray-300'
      )}
    >
      {label}
    </button>
  );
}

function formatTimestamp(timestamp: string, range: TimeRange): string {
  const date = new Date(timestamp);
  if (range === '24h') {
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  } else if (range === '7d') {
    return date.toLocaleDateString('en-US', { weekday: 'short', hour: '2-digit' });
  }
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function TimeSeriesChart() {
  const [range, setRange] = useState<TimeRange>('24h');
  const { data, isLoading, error } = useDashboardTimeSeries(range);
  const theme = useChartTheme();
  const succeeded = theme.status.good;
  const failed = theme.status.critical;

  const chartData = data?.data.map((dp) => ({
    timestamp: dp.timestamp,
    label: formatTimestamp(dp.timestamp, range),
    Succeeded: dp.succeeded,
    Failed: dp.failed,
    'Avg Latency': dp.avgLatencyMs,
  })) ?? [];

  return (
    <Card>
      <CardContent className="p-4">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <p className="text-[10px] font-medium uppercase tracking-wider text-emerald-500">// Message_Volume</p>
            <h3 className="mt-1 text-sm font-semibold uppercase tracking-wider text-white">Processing Trend</h3>
          </div>
          <div className="flex items-center gap-2">
            <RangeButton range="24h" currentRange={range} onClick={setRange} label="24H" />
            <RangeButton range="7d" currentRange={range} onClick={setRange} label="7D" />
            <RangeButton range="30d" currentRange={range} onClick={setRange} label="30D" />
          </div>
        </div>

        {isLoading ? (
          <div className="flex h-[250px] items-center justify-center">
            <LoadingSpinner size="md" />
          </div>
        ) : error ? (
          <div className="flex h-[250px] items-center justify-center text-xs text-red-400">
            Failed to load chart data
          </div>
        ) : chartData.length === 0 ? (
          <div className="flex h-[250px] items-center justify-center text-xs text-gray-500">
            No data available for this time range
          </div>
        ) : (
          <>
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorSucceeded" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={succeeded} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={succeeded} stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorFailed" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={failed} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={failed} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.grid} vertical={false} />
                  <XAxis
                    dataKey="label"
                    tick={{ fill: theme.axis, fontSize: 10 }}
                    tickLine={false}
                    axisLine={{ stroke: theme.grid }}
                  />
                  <YAxis
                    tick={{ fill: theme.axis, fontSize: 10 }}
                    tickLine={false}
                    axisLine={false}
                    width={40}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: theme.tooltipBg,
                      border: `1px solid ${theme.tooltipBorder}`,
                      borderRadius: '8px',
                      fontSize: '12px',
                    }}
                    labelStyle={{ color: theme.muted, marginBottom: '4px' }}
                    itemStyle={{ padding: '2px 0' }}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: '10px', paddingTop: '10px' }}
                    iconType="circle"
                    iconSize={8}
                  />
                  <Area
                    type="monotone"
                    dataKey="Succeeded"
                    stroke={succeeded}
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorSucceeded)"
                  />
                  <Area
                    type="monotone"
                    dataKey="Failed"
                    stroke={failed}
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorFailed)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Summary Stats */}
            {data?.summary && (
              <div className="mt-4 grid grid-cols-4 gap-4 border-t border-white/10 pt-4">
                <div className="text-center">
                  <p className="text-lg font-bold text-emerald-400">{data.summary.totalSucceeded.toLocaleString()}</p>
                  <p className="text-[10px] uppercase text-gray-500">Succeeded</p>
                </div>
                <div className="text-center">
                  <p className={cn('text-lg font-bold', data.summary.totalFailed > 0 ? 'text-red-400' : 'text-white')}>
                    {data.summary.totalFailed.toLocaleString()}
                  </p>
                  <p className="text-[10px] uppercase text-gray-500">Failed</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-bold text-white">{data.summary.avgLatencyMs}ms</p>
                  <p className="text-[10px] uppercase text-gray-500">Avg Latency</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-bold text-white">
                    {data.summary.peakTime
                      ? formatTimestamp(data.summary.peakTime, range)
                      : '—'}
                  </p>
                  <p className="text-[10px] uppercase text-gray-500">Peak Time</p>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
